import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Cog6ToothIcon,
  PaperAirplaneIcon,
  StopIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import useVoiceSession from '../../hooks/useVoiceSession';
import { RATE_OPTIONS } from '../../hooks/useSpeechOutput';
import {
  SEND_WORD_PRESETS,
  getSendWord,
  setSendWord as persistSendWord,
} from '../../lib/sendWord';

/**
 * The full-screen hands-free surface, shared by both voice modes.
 *
 * Everything about turn-taking lives in useVoiceSession; this is the face of
 * it. The one rule the layout obeys: turn state must be unmissable. If you
 * can't tell at a glance whether the mic is live, you talk into a closed mic
 * and lose the thought — the worst failure this feature can have.
 */

const BARGE_KEY = 'ctr.voice.bargeIn';

function loadBargeIn() {
  try {
    return localStorage.getItem(BARGE_KEY) === '1';
  } catch {
    return false;
  }
}

export default function VoiceSession({ conversationId, mode, onModeChange, onClose }) {
  const [notice, setNotice] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [sendWord, setSendWordState] = useState(getSendWord);
  const [bargeIn, setBargeInState] = useState(loadBargeIn);
  const scrollRef = useRef(null);

  const handleNotice = useCallback((msg) => {
    setNotice(msg || '');
    if (msg) setTimeout(() => setNotice(''), 5000);
  }, []);

  const session = useVoiceSession({
    conversationId,
    mode,
    bargeIn,
    sendWord,
    onNotice: handleNotice,
  });

  const {
    status,
    messages,
    pending,
    transcribing,
    errorText,
    level,
    threshold,
    speech,
    send,
    interrupt,
    canSend,
  } = session;

  const walkie = mode === 'walkie';

  const setBargeIn = (next) => {
    setBargeInState(next);
    try {
      localStorage.setItem(BARGE_KEY, next ? '1' : '0');
    } catch {
      /* private mode */
    }
  };

  const updateSendWord = (word) => setSendWordState(persistSendWord(word));

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pending, status]);

  // Escape ends the session — the fastest way out on a laptop.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const statusLine = () => {
    switch (status) {
      case 'connecting':
        return 'Warming up the microphone…';
      case 'hearing':
        return walkie ? 'Hearing you — keep going' : 'Hearing you…';
      case 'listening':
        return walkie
          ? `Your turn — say “${sendWord}” or tap Send when you're done`
          : 'Listening — pause when you\'re done and I\'ll answer';
      case 'thinking':
        return 'Thinking…';
      case 'speaking':
        return bargeIn ? 'Speaking — talk over it or tap Stop' : 'Speaking — tap Stop to cut in';
      case 'error':
        return errorText || 'Something went wrong.';
      default:
        return '';
    }
  };

  const tone =
    status === 'error' || status === 'speaking'
      ? 'text-rose-600 dark:text-rose-400'
      : status === 'thinking' || status === 'connecting'
        ? 'text-gray-500 dark:text-gray-400'
        : 'text-emerald-700 dark:text-emerald-400';

  return createPortal(
    <div className="fixed inset-0 z-50 flex flex-col bg-white dark:bg-gray-950">
      <header className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-800">
        <div
          role="radiogroup"
          aria-label="Voice mode"
          className="flex p-0.5 rounded-lg bg-gray-100 dark:bg-gray-800"
        >
          {[
            { id: 'conversation', label: 'Conversation' },
            { id: 'walkie', label: 'Walkie-talkie' },
          ].map((m) => (
            <button
              key={m.id}
              type="button"
              role="radio"
              aria-checked={mode === m.id}
              onClick={() => onModeChange(m.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                mode === m.id
                  ? 'bg-white dark:bg-gray-900 text-indigo-600 dark:text-indigo-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setShowSettings((s) => !s)}
          aria-label="Voice settings"
          aria-expanded={showSettings}
          className={`p-2 rounded-lg ${
            showSettings
              ? 'bg-gray-100 dark:bg-gray-800 text-indigo-600 dark:text-indigo-400'
              : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          <Cog6ToothIcon className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={onClose}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
        >
          <XMarkIcon className="w-4 h-4" />
          End
        </button>
      </header>

      {showSettings && (
        <SettingsPanel
          walkie={walkie}
          sendWord={sendWord}
          onSendWord={updateSendWord}
          bargeIn={bargeIn}
          onBargeIn={setBargeIn}
          speech={speech}
          level={level}
          threshold={threshold}
        />
      )}

      {notice && (
        <p className="px-4 py-2 text-xs text-center text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40">
          {notice}
        </p>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-3">
        {messages.length === 0 && !pending && status !== 'error' && (
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-8 max-w-sm mx-auto">
            {walkie
              ? `Talk as long as you like — nothing is sent until you say “${sendWord}” or tap Send. It can't cut you off.`
              : "Just start talking. Pause for about three seconds when you're done and I'll answer out loud."}
          </p>
        )}

        {messages.map((m) => (
          <div
            key={m.id}
            className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                m.role === 'user'
                  ? 'bg-indigo-600 text-white rounded-br-sm'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-sm'
              }`}
            >
              {m.text || <TypingDots />}
            </div>
          </div>
        ))}

        {/* The turn you're still building — dashed, so it reads as not-yet-sent. */}
        {pending && (
          <div className="flex justify-end">
            <div className="max-w-[85%] rounded-2xl rounded-br-sm border-2 border-dashed border-indigo-400 dark:border-indigo-600 px-4 py-2.5 text-sm text-gray-800 dark:text-gray-100 whitespace-pre-wrap">
              {pending}
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-gray-200 dark:border-gray-800 px-4 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <VoiceOrb status={status} />
          <p className={`min-w-0 flex-1 text-sm font-semibold ${tone}`}>{statusLine()}</p>
          {transcribing > 0 && (
            <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
              ✍ {transcribing}
            </span>
          )}
        </div>

        <ActionButton
          status={status}
          walkie={walkie}
          canSend={canSend}
          onSend={send}
          onInterrupt={interrupt}
          onClose={onClose}
        />
      </div>
    </div>,
    document.body
  );
}

/** The one big button. Which one it is, is the mode's whole personality. */
function ActionButton({ status, walkie, canSend, onSend, onInterrupt, onClose }) {
  if (status === 'error') {
    return (
      <button
        type="button"
        onClick={onClose}
        className="w-full h-12 rounded-xl border border-gray-300 dark:border-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-200"
      >
        Close
      </button>
    );
  }
  // Stop works while it's still thinking too — a turn you regret shouldn't
  // hold the floor until the reply finishes generating.
  if (status === 'speaking' || status === 'thinking') {
    return (
      <button
        type="button"
        onClick={onInterrupt}
        className="w-full h-14 flex items-center justify-center gap-2 rounded-2xl bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white text-lg font-bold shadow-lg transition-colors"
      >
        <StopIcon className="w-6 h-6" />
        {status === 'thinking' ? 'Cancel' : 'Stop'}
      </button>
    );
  }
  if (status === 'connecting') {
    return (
      <button
        type="button"
        disabled
        className="w-full h-14 rounded-2xl bg-gray-200 dark:bg-gray-800 text-gray-500 dark:text-gray-400 text-lg font-bold cursor-default"
      >
        Starting…
      </button>
    );
  }
  if (!walkie) {
    // Conversation mode has no send — the pause is the send.
    return (
      <p className="text-center text-xs text-gray-500 dark:text-gray-400 py-3">
        Hands-free — a pause ends your turn. Switch to Walkie-talkie above if you
        want to hold the floor.
      </p>
    );
  }
  return (
    <button
      type="button"
      onClick={onSend}
      disabled={!canSend}
      className="w-full h-14 flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-lg font-bold shadow-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
    >
      <PaperAirplaneIcon className="w-5 h-5" />
      Send
    </button>
  );
}

function SettingsPanel({
  walkie,
  sendWord,
  onSendWord,
  bargeIn,
  onBargeIn,
  speech,
  level,
  threshold,
}) {
  const { voices, rate, setRate, voiceURI, setVoiceURI } = speech;
  const englishFirst = [...voices].sort(
    (a, b) => Number(/^en/i.test(b.lang)) - Number(/^en/i.test(a.lang))
  );

  return (
    <div className="px-4 py-3 space-y-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/60">
      {walkie && (
        <div>
          <label
            htmlFor="send-word"
            className="block text-xs font-semibold text-gray-700 dark:text-gray-200"
          >
            Send word
          </label>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            Saying this at the end of a sentence ships your turn. A single word is
            quickest; two words almost never come up by accident.
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <input
              id="send-word"
              value={sendWord}
              onChange={(e) => onSendWord(e.target.value)}
              className="px-2.5 py-1.5 w-36 text-sm rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 dark:text-white"
            />
            {SEND_WORD_PRESETS.map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => onSendWord(w)}
                className={`px-2 py-1 rounded-md text-xs font-medium ${
                  sendWord === w
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
              >
                {w}
              </button>
            ))}
          </div>
        </div>
      )}

      <label className="flex items-start gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          checked={bargeIn}
          onChange={(e) => onBargeIn(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded accent-indigo-600"
        />
        <span className="min-w-0">
          <span className="block text-xs font-semibold text-gray-700 dark:text-gray-200">
            Interrupt by talking
          </span>
          <span className="block text-xs text-gray-500 dark:text-gray-400">
            Keeps the mic open while it speaks. On a phone speaker it can hear
            itself and cut off for no reason — best with headphones. The Stop
            button always works either way.
          </span>
        </span>
      </label>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">Speed</span>
          {RATE_OPTIONS.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRate(r)}
              className={`px-2 py-1 rounded-md text-xs font-medium ${
                rate === r
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
              }`}
            >
              {r}×
            </button>
          ))}
        </div>
        {englishFirst.length > 0 && (
          <label className="flex items-center gap-1.5 min-w-0">
            <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">Voice</span>
            <select
              value={voiceURI}
              onChange={(e) => setVoiceURI(e.target.value)}
              className="max-w-[12rem] px-2 py-1 text-xs rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 dark:text-white"
            >
              <option value="">Automatic</option>
              {englishFirst.map((v) => (
                <option key={v.voiceURI} value={v.voiceURI}>
                  {v.name} ({v.lang})
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      {/* Live mic level against the adaptive speech threshold. A phone has no
          console, and this is the only way to see why a turn won't end. */}
      <div>
        <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-800 overflow-hidden">
          <div
            className={`h-full transition-[width] duration-150 ${
              level >= threshold ? 'bg-indigo-500' : 'bg-gray-400'
            }`}
            style={{ width: `${Math.min(100, (level / (threshold * 3 || 1)) * 100)}%` }}
          />
        </div>
        <p className="mt-1 text-xs font-mono text-gray-500 dark:text-gray-400 text-center">
          mic {level.toFixed(3)} · speech ≥ {threshold.toFixed(3)}
        </p>
      </div>
    </div>
  );
}

function TypingDots() {
  return (
    <span className="flex gap-1 py-1" aria-label="thinking">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-gray-400 dark:bg-gray-500 animate-bounce"
          style={{ animationDelay: `${i * 120}ms` }}
        />
      ))}
    </span>
  );
}

function VoiceOrb({ status }) {
  if (status === 'hearing') {
    return (
      <span className="flex items-end gap-0.5 h-9 shrink-0" aria-label="hearing you">
        {[0, 1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className="w-1.5 rounded-full bg-emerald-500 animate-pulse"
            style={{
              height: `${10 + (i % 2 === 0 ? 10 : 20)}px`,
              animationDelay: `${i * 90}ms`,
            }}
          />
        ))}
      </span>
    );
  }
  if (status === 'thinking') {
    return (
      <span className="flex items-center gap-1 h-9 shrink-0" aria-label="thinking">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
            style={{ animationDelay: `${i * 150}ms` }}
          />
        ))}
      </span>
    );
  }
  const color =
    status === 'error'
      ? 'bg-rose-500'
      : status === 'connecting'
        ? 'bg-gray-400'
        : status === 'speaking'
          ? 'bg-rose-500'
          : 'bg-emerald-500';
  const ping = status === 'listening' || status === 'speaking' || status === 'connecting';
  return (
    <span className="relative flex h-9 w-9 items-center justify-center shrink-0" aria-label={status}>
      {ping && (
        <span className={`absolute inline-flex h-9 w-9 rounded-full opacity-30 ${color} animate-ping`} />
      )}
      <span className={`relative inline-flex h-6 w-6 rounded-full ${color}`} />
    </span>
  );
}
